import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { useUpdate } from '@client/new/helpers/useUpdate';
import _ from 'lodash';
import { UploadMap } from './videos.domain';
import { uploadFile } from '../../creator/library/models/upload.hooks';
import { useEffect, useState } from 'react';
import { useUpload, VideoById } from '@client/new/common/videos/useUpload';
import { loadVideo } from '@client/new/helpers/load-video-from-file';
import { getFileNameWihoutExtension } from '@client/new/common/videos/helpers';
import { trackEvent } from '@services/tracking';
import toast from 'react-hot-toast';
import { ContentFilters } from '@client/new/common/library/helpers';
import { Media } from './workout.domain';

type PayloadMap = {
  onDrop: { file: File; videoType: ContentFilters | null };
  upload: never;
  editName: string;
  thumbnailTimestamp: number;
  cancel: never;
};
type VideosModelAction = Actions<PayloadMap>;
export type UploadToLibraryModelDispatcher = FSpread<PayloadMap>;

type VideosVMState = {
  uploadFile: File | null;
  thumbnailTimestamp: number;
  isUploading: boolean;
  isProcessing: boolean;
  isUploadFinished: boolean;
  uploadedId: string | null;
  validationError?: { title: string; description: string };
};

const initialState: VideosVMState = {
  uploadFile: null,
  thumbnailTimestamp: 0,
  isUploading: false,
  uploadedId: null,
  isProcessing: false,
  isUploadFinished: false,
  validationError: undefined,
};

export type UploadToLibraryModel = {
  dispatch: UploadToLibraryModelDispatcher;
  uploadFile: File | null;
  progress: number;
} & Pick<
  VideosVMState,
  | 'thumbnailTimestamp'
  | 'isUploading'
  | 'isProcessing'
  | 'isUploadFinished'
  | 'validationError'
>;

const singleFileId = '1';

const getUploadValidationError = (type: ContentFilters, duration: number) => {
  switch (type) {
    case 'short':
      return duration < 60
        ? undefined
        : {
            title: 'Video too long',
            description:
              'Your video file is too long. Short-form videos must be under 60 seconds.',
          };
    case 'long':
      return duration >= 60
        ? undefined
        : {
            title: 'Video too short',
            description:
              'Your video file is too short. Long-form videos must be at least 60 seconds long.',
          };
  }
};

export const useUploadToLibraryModel = (p: {
  influencerId: number;
  type: string | null;
  onAddVideo: (video: {
    uuid: string;
    title: string;
    thumbnailTimestampMs: number;
    previewUrl?: string;
  }) => void;
  skipProcessing?: boolean;
  tags?: string[];
  attachOnUpload?: boolean;
}): UploadToLibraryModel => {
  const [state, updateState] = useUpdate(initialState);
  const [uploadMap, setUploadMap] = useState<UploadMap>({});

  const { uploadFinished, startPolling } = useUpload({
    skipProcessing: p.skipProcessing,
    influencerId: p.influencerId,
    uploadedId: state.uploadedId,
    addVideo: (v) => {
      if (!v) throw new Error('No video returned from upload');
      p.onAddVideo({
        uuid: v.uuid,
        title: v.title,
        thumbnailTimestampMs: state.thumbnailTimestamp * 1000,
      });
    },
  });

  useEffect(() => {
    if (uploadFinished && state.uploadedId) {
      updateState({ isUploadFinished: true, uploadedId: null });
    }
  }, [state.uploadedId, updateState, uploadFinished]);

  const dispatch: UploadToLibraryModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as VideosModelAction;
    switch (action.type) {
      case 'onDrop': {
        onDrop(action.payload);
        return null;
      }
      case 'upload':
        upload();
        return null;
      case 'cancel': {
        if (uploadMap[singleFileId]) {
          uploadMap[singleFileId]?.muxInstance &&
            uploadMap[singleFileId].muxInstance.abort();
        }
        updateState(initialState);
        return null;
      }
      case 'editName': {
        if (state.uploadFile) {
          const file = new File([state.uploadFile], `${action.payload}`, {
            type: state.uploadFile.type,
          });
          updateState({ uploadFile: file });
        }
        return null;
      }
      case 'thumbnailTimestamp':
        return updateState({ thumbnailTimestamp: action.payload });
    }
  };

  const onDrop = ({
    file,
    videoType,
  }: {
    file: File;
    videoType: ContentFilters | null;
  }) => {
    updateState({ validationError: undefined });
    if (!videoType) {
      updateState({ uploadFile: file });
      return;
    }

    loadVideo(file).then((video) => {
      const validationError = getUploadValidationError(
        videoType,
        video.duration,
      );
      validationError
        ? updateState({ validationError })
        : updateState({ uploadFile: file });
    });
  };

  const onUploadSuccess = (uploadedId: string) => {
    if (p.attachOnUpload) {
      // The post can be created while Mux is still encoding: attach the video
      // by uuid as soon as the file upload finishes, without waiting for the
      // encode/processing poll. The community webhook publishes the post once
      // encoding completes.
      p.onAddVideo({
        uuid: uploadedId,
        title: state.uploadFile
          ? getFileNameWihoutExtension(state.uploadFile.name)
          : '',
        thumbnailTimestampMs: state.thumbnailTimestamp * 1000,
        // Mux has not encoded a thumbnail yet, so hand the caller a local
        // object URL of the just-uploaded file to preview while it processes.
        previewUrl: state.uploadFile
          ? URL.createObjectURL(state.uploadFile)
          : undefined,
      });
      updateState({ isUploadFinished: true, uploadedId: null });
      return;
    }
    updateState({ isProcessing: true, uploadedId });
    startPolling(3000);
  };

  const upload = async () => {
    updateState({ isUploading: true });
    try {
      if (!state.uploadFile) return;
      trackEvent({
        type: 'Video Upload Started',
        payload: { 'Number of Videos': 1 },
      });
      await uploadFile({
        video: {
          file: state.uploadFile,
          thumbnailTimestamp: state.thumbnailTimestamp,
          id: singleFileId,
        },
        influencerId: p.influencerId,
        setUploadMap,
        uploadMap,
        onSuccess: (uploadedId: string) => onUploadSuccess(uploadedId),
        folderId: null,
        tags: p.tags,
      });
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      updateState({ isUploading: false });
    }
  };
  const progress = _.isEmpty(uploadMap) ? 0 : uploadMap[singleFileId].progress;

  return {
    dispatch,
    uploadFile: state.uploadFile,
    thumbnailTimestamp: state.thumbnailTimestamp,
    progress,
    isProcessing: state.isProcessing,
    isUploading: state.isUploading,
    isUploadFinished: state.isUploadFinished,
    validationError: state.validationError,
  };
};
